import { useCallback } from 'react';
import { App } from 'antd';
import { useForm } from 'antd/es/form/Form';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CalendarEvent, CalendarModalBody } from 'entities/calendar';
import { UpdateCalendarEventFooter } from '../ui';
import { UpdateCalendarEventForm } from '../ui/UpdateCalendarEventForm';

type FormValues = Omit<CalendarEvent, 'startDate' | 'endDate'> & {
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
};

const useUpdateEventModal = (): ((eventData: CalendarEvent) => void) => {
  const { modal } = App.useApp();
  const [form] = useForm<FormValues>();
  const { calendarId } = useParams();

  const { t } = useTranslation('Calendar');

  const openUpdateModal = useCallback(
    (eventData: CalendarEvent): void => {
      const { startDate, endDate, ...restData } = eventData;
      form.setFieldsValue({
        ...restData,
        dateRange: [dayjs(startDate), dayjs(endDate)],
      });

      const instance = modal.confirm({
        centered: true,
        closable: true,
        maskClosable: true,
        destroyOnClose: true,
        okButtonProps: { htmlType: 'submit' },
        cancelButtonProps: { htmlType: 'reset' },
        okText: t('calendarModal.okBtn.text'),
        width: 450,
        icon: null,
        onOk: () => {
          return Promise.reject();
        },
        footer: (_, { CancelBtn, OkBtn }) => (
          <UpdateCalendarEventFooter
            calendarId={calendarId as string}
            eventId={eventData.id}
            closeModal={() => instance.destroy()}
          >
            <CancelBtn />
            <OkBtn />
          </UpdateCalendarEventFooter>
        ),
        modalRender: (dom) => (
          <UpdateCalendarEventForm
            eventData={eventData}
            calendarId={calendarId as string}
            form={form}
            dom={dom}
            instance={instance}
          />
        ),
        content: <CalendarModalBody form={form} calendarId={calendarId as string} eventId={eventData.id} />,
      });
    },
    [modal, t, form, calendarId],
  );

  return openUpdateModal;
};

export { useUpdateEventModal };
